// Wastage limits (QA IS4-002, owner decision ID14).
//
// Two owner settings, in the tenant currency:
//   - cashier record limit: a cashier's wastage entry up to this value posts at
//     once; above it the entry waits for approval and its stock is held.
//   - manager approve limit: a manager records and approves up to this value;
//     above it only the owner approves. Blank = no limit.
// The same rule covers the Damage / Wastage / Expiry write-offs on Adjust Stock.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Skeleton,
  Alert,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { amountToMajor, getCurrencyDecimals, getSubunitMultiplier, majorToAmount } from '@bbpos/utils';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { wastageService, type WastageLimits } from '@/services/wastage.service';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { SaveStatus } from '@/components/SaveStatus';

export function WastageLimitsPage() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const decimals = getCurrencyDecimals(currency);
  // Minor units → plain major-unit string (no grouping) to seed an input.
  const toMajorInput = (minor: number): string =>
    (minor / getSubunitMultiplier(currency)).toFixed(decimals);

  const [saved, setSaved] = useState<WastageLimits | null>(null);
  const [cashierInput, setCashierInput] = useState('0');
  const [managerInput, setManagerInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    wastageService
      .getLimits()
      .then((res) => {
        if (cancelled) return;
        setSaved(res);
        setCashierInput(toMajorInput(Number(res.cashier_record_limit)));
        setManagerInput(res.manager_approve_limit == null ? '' : toMajorInput(Number(res.manager_approve_limit)));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load wastage limits');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Load once; the currency only formats the inputs.
  }, []);

  /** '' → null (manager: no limit); a valid amount → minor units; else undefined (invalid). */
  const parseAmount = (text: string, blankIsNull: boolean): number | null | undefined => {
    if (text.trim() === '') return blankIsNull ? null : undefined;
    const n = Number(text);
    return Number.isFinite(n) && n >= 0 ? majorToAmount(n, currency) : undefined;
  };
  const cashierLimit = parseAmount(cashierInput, false);
  const managerLimit = parseAmount(managerInput, true);
  const cashierValid = cashierLimit !== undefined;
  const managerValid = managerLimit !== undefined;
  const dirty = saved !== null && (
    !cashierValid || !managerValid
    || cashierLimit !== Number(saved.cashier_record_limit)
    || managerLimit !== (saved.manager_approve_limit == null ? null : Number(saved.manager_approve_limit))
  );
  const canSave = !saving && saved !== null && cashierValid && managerValid && dirty;
  const { dialog: leaveDialog } = useUnsavedChangesGuard(dirty);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await wastageService.setLimits({
        cashier_record_limit: cashierLimit as number,
        manager_approve_limit: managerLimit as number | null,
      });
      setSaved(res);
      setCashierInput(toMajorInput(Number(res.cashier_record_limit)));
      setManagerInput(res.manager_approve_limit == null ? '' : toMajorInput(Number(res.manager_approve_limit)));
      toast({ title: 'Wastage limits updated', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const describe = (minor: number | null) =>
    minor == null ? 'no limit' : `${symbol}${amountToMajor(minor, currency)}`;

  return (
    <PageContainer
      title="Wastage limits"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Settings', href: '/settings' },
        { label: 'Wastage limits' },
      ]}
    >
      <ErrorBoundary resetKey="wastage-limits">
        <div className="bg-gradient-to-r from-warning-500 to-danger-600 rounded-2xl p-6 text-white shadow-lg mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-white">Who can write off stock?</h1>
          <p className="mt-1.5 text-sm text-white/85 max-w-2xl">
            Wastage above a limit waits for approval in Approvals. Until then its stock is held:
            it still counts as on hand, but the till cannot sell it. The same limits apply to the
            Damage, Wastage and Expiry write-offs on Adjust Stock.
          </p>
        </div>

        {error && (
          <div className="mb-5">
            <Alert variant="danger" title="Couldn't load the wastage limits" description={error} />
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <Skeleton key={i} variant="rect" height={96} />
            ))}
          </div>
        ) : saved && (
          <div className={cn(GLASS_FORM, 'p-5 space-y-5 animate-slide-up')}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 shrink-0 rounded-lg bg-gradient-to-br from-teal-500 to-success-500 flex items-center justify-center text-white font-bold">
                C
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-neutral-900 dark:text-white">Cashier record limit</h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 mb-3">
                  A cashier&rsquo;s wastage up to this value is recorded at once. Above it, a manager
                  or the owner must approve it. 0 sends every cashier entry for approval.
                </p>
                <div className="max-w-xs">
                  <Input
                    label={`Cashier limit (${symbol})`}
                    type="number"
                    min={0}
                    step={1 / getSubunitMultiplier(currency)}
                    value={cashierInput}
                    onChange={(v: string) => setCashierInput(v)}
                    error={cashierValid ? undefined : 'Enter an amount of 0 or more'}
                    helperText={`Currently ${describe(Number(saved.cashier_record_limit))}`}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 pt-5 border-t border-neutral-200 dark:border-neutral-700">
              <div className="w-10 h-10 shrink-0 rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                M
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-neutral-900 dark:text-white">Manager approve limit</h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 mb-3">
                  A manager records and approves wastage up to this value. Above it, only the owner
                  can approve. Leave it blank for no limit.
                </p>
                <div className="max-w-xs">
                  <Input
                    label={`Manager limit (${symbol})`}
                    type="number"
                    min={0}
                    step={1 / getSubunitMultiplier(currency)}
                    value={managerInput}
                    placeholder="No limit"
                    onChange={(v: string) => setManagerInput(v)}
                    error={managerValid ? undefined : 'Enter an amount of 0 or more, or leave it blank'}
                    helperText={`Currently ${describe(saved.manager_approve_limit == null ? null : Number(saved.manager_approve_limit))}`}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <SaveStatus dirty={dirty} />
              <Link to="/settings" className="text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300">
                Cancel
              </Link>
              <Button variant="primary" onClick={handleSave} disabled={!canSave}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}
        {leaveDialog}
      </ErrorBoundary>
    </PageContainer>
  );
}
